import React, { useEffect, useState, useCallback } from 'react'
import {
  Typography,
  Stack,
  CardContent,
  Card,
  Box,
  CircularProgress,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Tooltip,
  Divider,
  Alert,
} from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import stylesheet from '../detail-container.stylesheet'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { setRPMEnrollments } from '../../../../redux/patient-detail-slice'
import { PatientService } from '../../../../services/patient.service'
import {
  RPMEnrollment,
  RPMTimeSummary,
  RPMReadingSummary,
  RPMBillingPeriod,
  getTimeBillingTier,
  getReadingBillingTier,
  getConsentStatusColor,
  getRPMStatusColor,
  formatMinutes,
  formatSeconds,
  CONSENT_STATUS_DISPLAY,
  RPM_ACTIVITY_DISPLAY,
} from '../../../../model/rpm.types'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import EmptyCardComponent from '../common/empty-card.component'
import { toast } from 'react-toastify'
import Helper from '../../../../utils/helper'
import RPMEnrollmentDialog from './rpm-enrollment-dialog.component'
import RPMTimeEntryDialog from './rpm-time-entry.component'
import RPMTimeLogComponent from './rpm-time-log.component'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

interface IProps {
  classes: ClassNameMap
}

const patientService = new PatientService()

const RPMBillingComponent: React.FC<IProps> = ({ classes }) => {
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const enrollments = useSelector((state: RootState) => state.patientDetail.rpmEnrollments)
  const dispatch = useDispatch()
  const [timeSummary, setTimeSummary] = useState<RPMTimeSummary | null>(null)
  const [readingSummary, setReadingSummary] = useState<RPMReadingSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false)
  const [timeEntryDialogOpen, setTimeEntryDialogOpen] = useState(false)
  const [selectedEnrollment, setSelectedEnrollment] = useState<RPMEnrollment | null>(null)
  const [showTimeLog, setShowTimeLog] = useState(false)

  // Get current billing month (first of month)
  const getCurrentBillingMonth = () => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  }

  // Load summary data only (enrollments come from Redux, set by RPMActivityTimer)
  const loadSummaryData = useCallback(() => {
    if (patientDetail?.id) {
      setLoading(true)
      const billingMonth = getCurrentBillingMonth()

      Promise.all([
        patientService.getRPMTimeSummary(patientDetail.id, billingMonth),
        patientService.getRPMReadingSummary(patientDetail.id, billingMonth),
      ])
        .then(([timeRes, readingRes]) => {
          if (timeRes?.data?.success) {
            setTimeSummary(timeRes.data)
          }
          if (readingRes?.data?.success) {
            setReadingSummary(readingRes.data)
          }
        })
        .catch((err) => {
          console.error('Failed to load RPM data:', err)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [patientDetail?.id])

  // Full refresh after mutations (enrollment created, time entry added)
  const loadRPMData = useCallback(() => {
    if (patientDetail?.id) {
      setLoading(true)
      const billingMonth = getCurrentBillingMonth()

      Promise.all([
        patientService.getRPMEnrollments(patientDetail.id),
        patientService.getRPMTimeSummary(patientDetail.id, billingMonth),
        patientService.getRPMReadingSummary(patientDetail.id, billingMonth),
      ])
        .then(([enrollRes, timeRes, readingRes]) => {
          if (enrollRes?.data?.success) {
            dispatch(setRPMEnrollments(enrollRes.data.rpm_enrollments || []))
          }
          if (timeRes?.data?.success) {
            setTimeSummary(timeRes.data)
          }
          if (readingRes?.data?.success) {
            setReadingSummary(readingRes.data)
          }
        })
        .catch((err) => {
          console.error('Failed to load RPM data:', err)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [patientDetail?.id, dispatch])

  useEffect(() => {
    loadSummaryData()
  }, [loadSummaryData])

  // Listen for time entry saved from activity timer
  useEffect(() => {
    const handleTimerSaved = () => {
      loadRPMData()
    }
    window.addEventListener('rpm-time-entry-saved', handleTimerSaved)
    return () => {
      window.removeEventListener('rpm-time-entry-saved', handleTimerSaved)
    }
  }, [loadRPMData])

  // Get active enrollment
  const activeEnrollment = enrollments.find((e) => e.status === 'active')

  // Handle enrollment created — update Redux with response data, then refresh summaries only
  const handleEnrollmentCreated = (newEnrollments: RPMEnrollment[]) => {
    setEnrollDialogOpen(false)
    dispatch(setRPMEnrollments([...enrollments, ...newEnrollments]))
    loadSummaryData()
    toast.success('RPM enrollment created', Helper.bottom_center())
  }

  // Handle time entry added
  const handleTimeEntryAdded = () => {
    setTimeEntryDialogOpen(false)
    loadRPMData()
    toast.success('Time entry added', Helper.bottom_center())
  }

  // Calculate progress percentages
  const timeProgress = timeSummary ? Math.min((timeSummary.total_minutes / 20) * 100, 100) : 0
  const readingProgress = readingSummary ? Math.min((readingSummary.total_reading_days / 16) * 100, 100) : 0

  const timeTier = timeSummary ? getTimeBillingTier(timeSummary.total_minutes) : null
  const readingTier = readingSummary ? getReadingBillingTier(readingSummary.total_reading_days) : null

  // Generate alerts based on current state
  const generateAlerts = (): { severity: 'error' | 'warning' | 'info'; message: string }[] => {
    const alerts: { severity: 'error' | 'warning' | 'info'; message: string }[] = []

    if (!activeEnrollment) return alerts

    // Consent alert (blocking)
    if (activeEnrollment.consent_status !== 'obtained') {
      alerts.push({
        severity: 'error',
        message: 'Consent required before billing',
      })
    }

    // Reading days alert - only warn if they haven't reached ANY tier (< 2 days)
    // Once they have 2+ days, they've qualified for 99445, so no urgent alert needed
    if (readingSummary && readingTier) {
      const daysRemaining = readingSummary.days_remaining_in_month || 0
      const currentDays = readingSummary.total_reading_days || 0
      const daysNeededFor99445 = Math.max(0, 2 - currentDays)

      if (currentDays < 2 && daysNeededFor99445 > daysRemaining && daysRemaining > 0) {
        alerts.push({
          severity: 'warning',
          message: `Need ${daysNeededFor99445} more reading ${daysNeededFor99445 === 1 ? 'day' : 'days'} for 99445 but only ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left`,
        })
      } else if (currentDays === 0) {
        alerts.push({
          severity: 'info',
          message: 'No reading days recorded yet this month',
        })
      }
    }

    // Time alert - check if below billable threshold
    if (timeSummary) {
      const totalMinutes = timeSummary.total_minutes || 0
      if (totalMinutes < 10) {
        alerts.push({
          severity: 'info',
          message: `${10 - totalMinutes} more minutes needed for billable tier`,
        })
      }
    }

    return alerts
  }

  const alerts = activeEnrollment ? generateAlerts() : []

  if (loading) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        </CardContent>
      </Card>
    )
  }

  // If no active enrollment, show enrollment option
  if (!activeEnrollment) {
    return (
      <>
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" fontWeight="bold">
                RPM Billing
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddCircleOutlineIcon sx={{ fontSize: '0.875rem' }} />}
                onClick={() => setEnrollDialogOpen(true)}
                sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}
              >
                Enable RPM
              </Button>
            </Stack>
            <EmptyCardComponent
              msg="Patient is not enrolled in RPM. Click 'Enable RPM' to enroll this patient for remote patient monitoring billing."
            />
          </CardContent>
        </Card>
        <RPMEnrollmentDialog
          open={enrollDialogOpen}
          onClose={() => setEnrollDialogOpen(false)}
          onSuccess={handleEnrollmentCreated}
          patientId={patientDetail?.id}
        />
      </>
    )
  }

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="body2" fontWeight="bold">
              RPM Billing
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                size="small"
                label={activeEnrollment.program_name || 'RPM'}
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
              <Chip
                size="small"
                label={activeEnrollment.status}
                sx={{
                  fontSize: '0.7rem',
                  height: 20,
                  ...(activeEnrollment.status === 'active' && {
                    backgroundColor: 'var(--color-success) !important',
                    color: '#fff !important',
                  }),
                }}
                color={activeEnrollment.status !== 'active' ? getRPMStatusColor(activeEnrollment.status) : undefined}
              />
            </Stack>
          </Stack>

          {/* Alerts Section */}
          {alerts.length > 0 && (
            <Stack spacing={0.5} mb={1.5}>
              {alerts.map((alert, idx) => (
                <Alert
                  key={idx}
                  severity={alert.severity}
                  icon={
                    alert.severity === 'error' ? (
                      <ErrorOutlineIcon sx={{ fontSize: '1rem' }} />
                    ) : alert.severity === 'warning' ? (
                      <WarningIcon sx={{ fontSize: '1rem' }} />
                    ) : undefined
                  }
                  sx={{
                    py: 0,
                    px: 1,
                    '& .MuiAlert-message': { py: 0.5, fontSize: '0.75rem' },
                    '& .MuiAlert-icon': { py: 0.5, mr: 0.5 },
                  }}
                >
                  {alert.message}
                </Alert>
              ))}
            </Stack>
          )}

          {/* Consent Status */}
          <Box mb={1.5} p={1} sx={{ bgcolor: 'var(--color-background-subtle)', borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {activeEnrollment.consent_status === 'obtained' ? (
                <CheckCircleIcon sx={{ fontSize: '1rem', color: 'var(--color-success)' }} />
              ) : (
                <WarningIcon color="warning" sx={{ fontSize: '1rem' }} />
              )}
              <Typography variant="caption">
                Consent: {CONSENT_STATUS_DISPLAY[activeEnrollment.consent_status]}
              </Typography>
              {activeEnrollment.consent_status === 'obtained' && activeEnrollment.consent_method && (
                <Typography variant="caption" color="text.secondary">
                  ({activeEnrollment.consent_method})
                </Typography>
              )}
              {activeEnrollment.consent_status !== 'obtained' && (
                <Button size="small" variant="text" sx={{ ml: 'auto', fontSize: '0.7rem', py: 0 }}>
                  Record Consent
                </Button>
              )}
            </Stack>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Time Tracking Section */}
          <Box mb={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <AccessTimeIcon sx={{ fontSize: '1rem' }} color="action" />
                <Typography variant="caption" fontWeight="medium">
                  Time This Month
                </Typography>
              </Stack>
              <Button
                size="small"
                variant="outlined"
                className={classes.taskAddBtn}
                startIcon={<AddCircleOutlineIcon sx={{ fontSize: '0.8rem !important' }} />}
                onClick={() => {
                  setSelectedEnrollment(activeEnrollment)
                  setTimeEntryDialogOpen(true)
                }}
                sx={{ fontSize: '0.7rem', py: 0.3, px: 0.75 }}
              >
                Add Time
              </Button>
            </Stack>

            <Box mb={0.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                  <Typography variant="body1" fontWeight="medium" fontFamily="monospace">
                    {formatSeconds(timeSummary?.total_seconds || (timeSummary?.total_minutes || 0) * 60)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({timeSummary?.total_minutes || 0} min billable)
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {timeTier?.description || 'Not billable'}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={timeProgress}
                sx={{ height: 6, borderRadius: 1, mt: 0.5 }}
                color={timeSummary && timeSummary.total_minutes >= 20 ? 'success' : 'primary'}
              />
              {timeTier && timeTier.minutes_to_next_tier !== undefined && timeTier.minutes_to_next_tier > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {timeTier.minutes_to_next_tier} min to next tier
                </Typography>
              )}
              {/* Role breakdown - always show when there's time tracked */}
              {timeSummary && (timeSummary.total_seconds > 0 || timeSummary.total_minutes > 0) && (() => {
                // Calculate total seconds, falling back to minutes if seconds not available
                const totalSecs = timeSummary.total_seconds || (timeSummary.total_minutes || 0) * 60
                // If by_role is available use it, otherwise attribute all to navigator (default)
                const providerSecs = timeSummary.by_role?.provider_seconds || 0
                const navigatorSecs = timeSummary.by_role?.navigator_seconds || (timeSummary.by_role ? 0 : totalSecs)
                return (
                  <Stack direction="row" spacing={1.5} mt={0.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Provider: {formatSeconds(providerSecs)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Navigator: {formatSeconds(navigatorSecs)}
                    </Typography>
                  </Stack>
                )
              })()}
            </Box>

            {/* Billable codes */}
            {timeTier && timeTier.codes.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {timeTier.codes.map((code, idx) => (
                  <Chip
                    key={idx}
                    size="small"
                    label={code}
                    sx={{
                      fontSize: '0.65rem',
                      height: 18,
                      backgroundColor: 'var(--color-success) !important',
                      color: '#fff !important',
                    }}
                  />
                ))}
              </Stack>
            )}

            {/* Time Log Toggle */}
            <Button
              size="small"
              variant="text"
              onClick={() => setShowTimeLog(!showTimeLog)}
              endIcon={showTimeLog ? <ExpandLessIcon sx={{ fontSize: '1rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
              sx={{ mt: 0.5, p: 0, fontSize: '0.75rem' }}
            >
              {showTimeLog ? 'Hide' : 'View'} Time Entries
            </Button>

            {/* Time Log */}
            {showTimeLog && (
              <Box mt={1}>
                <RPMTimeLogComponent
                  patientId={patientDetail?.id}
                  rpmEnrollmentId={activeEnrollment?.id}
                  billingMonth={getCurrentBillingMonth()}
                  onEntryDeleted={loadRPMData}
                />
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Reading Days Section */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
              <CalendarTodayIcon sx={{ fontSize: '1rem' }} color="action" />
              <Typography variant="caption" fontWeight="medium">
                Reading Days This Month
              </Typography>
            </Stack>

            <Box mb={0.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="body1" fontWeight="medium">
                  {readingSummary?.total_reading_days || 0} days
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {readingTier?.description || 'Not billable'}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={readingProgress}
                sx={{ height: 6, borderRadius: 1, mt: 0.5 }}
                color={readingSummary && readingSummary.total_reading_days >= 16 ? 'success' : 'primary'}
              />
              {readingTier && readingTier.days_to_next_tier !== undefined && readingTier.days_to_next_tier > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {readingTier.days_to_next_tier} {readingTier.days_to_next_tier === 1 ? 'day' : 'days'} to next tier
                  {readingSummary && readingSummary.days_remaining_in_month > 0 && (
                    <> ({readingSummary.days_remaining_in_month} {readingSummary.days_remaining_in_month === 1 ? 'day' : 'days'} left in month)</>
                  )}
                </Typography>
              )}
            </Box>

            {/* Reading breakdown */}
            {readingSummary && (readingSummary.days_with_bp > 0 || readingSummary.days_with_weight > 0) && (
              <Stack direction="row" spacing={1}>
                {readingSummary.days_with_bp > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    BP: {readingSummary.days_with_bp} days
                  </Typography>
                )}
                {readingSummary.days_with_weight > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Weight: {readingSummary.days_with_weight} days
                  </Typography>
                )}
              </Stack>
            )}

            {/* Billable codes */}
            {readingTier && readingTier.code && (
              <Stack direction="row" spacing={0.5} mt={0.5}>
                <Chip
                  size="small"
                  label={readingTier.code}
                  sx={{
                    fontSize: '0.65rem',
                    height: 18,
                    backgroundColor: 'var(--color-success) !important',
                    color: '#fff !important',
                  }}
                />
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <RPMEnrollmentDialog
        open={enrollDialogOpen}
        onClose={() => setEnrollDialogOpen(false)}
        onSuccess={handleEnrollmentCreated}
        patientId={patientDetail?.id}
      />
      <RPMTimeEntryDialog
        open={timeEntryDialogOpen}
        onClose={() => setTimeEntryDialogOpen(false)}
        onSuccess={handleTimeEntryAdded}
        patientId={patientDetail?.id}
        rpmEnrollment={selectedEnrollment}
      />
    </>
  )
}

export default withStyles(stylesheet)(RPMBillingComponent)
